import { Button, Grid, Message, Modal } from "@intility/bifrost-react";
import { useState } from "react";
import {
  useApplyScimScope,
  useCreateScimToken,
  useRevokeScimToken,
  useScimScope,
  useScimTokens,
  useSetProvisioningMode,
} from "~/api/companies";
import type { OnboardedTenant } from "~/api/types";
import { CopyButton } from "~/components/CopyButton";

// kc.scim_mode_requires_token. The installed @intility/id-errors does not carry the constant yet.
const SCIM_MODE_REQUIRES_TOKEN = "kc.scim_mode_requires_token";

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : "Something went wrong.";

function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <Message state="alert" header="Failed">
      {errorText(error)}
    </Message>
  );
}

function Confirm({
  header,
  confirmLabel,
  pending,
  destructive = false,
  error,
  onConfirm,
  onClose,
  children,
}: {
  header: string;
  confirmLabel: string;
  pending: boolean;
  destructive?: boolean;
  error: unknown;
  onConfirm: () => void;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Modal
      isOpen
      header={header}
      width={480}
      onRequestClose={pending ? () => {} : onClose}
    >
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          {children}
        </p>
        <ErrorMessage error={error} />
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <Button
            variant="flat"
            onClick={onClose}
            state={pending ? "inactive" : undefined}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            state={pending ? "inactive" : destructive ? "alert" : undefined}
            onClick={pending ? undefined : onConfirm}
          >
            {pending ? "Working…" : confirmLabel}
          </Button>
        </div>
      </Grid>
    </Modal>
  );
}

// The token is in the answer of the create call only, so the dialog is the one chance to copy it.
function TokenDialog({
  token,
  onClose,
}: {
  token: string;
  onClose: () => void;
}) {
  return (
    <Modal isOpen header="SCIM token" width={560} onRequestClose={onClose}>
      <Grid gap={16}>
        <p className="bf-p" style={{ margin: 0 }}>
          Copy this token now. It is not shown again. Give it to the tenant
          administrator as the secret token of the provisioning job.
        </p>
        <code style={{ wordBreak: "break-all" }}>{token}</code>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <CopyButton value={token} small={false}>
            Copy token
          </CopyButton>
          <Button variant="filled" onClick={onClose}>
            Done
          </Button>
        </div>
      </Grid>
    </Modal>
  );
}

type Dialog = "mode" | "apply" | null;

// Provisioning mode, SCIM tokens and SCIM scope of one tenant (ADR-0062). The routes need
// admin on provider:intility, like the rest of the page.
export function ScimControls({
  companyId,
  tenant,
}: {
  companyId: string;
  tenant: OnboardedTenant;
}) {
  const ref = { companyId, tenantId: tenant.tenantId };
  const isScim = tenant.provisioningMode === "scim";
  const [dialog, setDialog] = useState<Dialog>(null);
  const [shownToken, setShownToken] = useState<string | null>(null);

  const setMode = useSetProvisioningMode();
  const createToken = useCreateScimToken();
  const revokeToken = useRevokeScimToken();
  const tokens = useScimTokens(ref, isScim);
  const scope = useScimScope(ref);
  const applyScope = useApplyScimScope();

  const close = () => {
    setDialog(null);
    setMode.reset();
    applyScope.reset();
  };

  // scim needs a live token first (409). Create one, show it, and repeat the switch.
  const switchMode = async () => {
    try {
      if (isScim) {
        await setMode.mutateAsync({ ...ref, mode: "graph" });
      } else {
        try {
          await setMode.mutateAsync({ ...ref, mode: "scim" });
        } catch (e) {
          if (
            !(e instanceof Error) ||
            (e as { code?: string }).code !== SCIM_MODE_REQUIRES_TOKEN
          )
            throw e;
          const created = await createToken.mutateAsync(ref);
          setShownToken(created.token);
          await setMode.mutateAsync({ ...ref, mode: "scim" });
        }
      }
      close();
    } catch {
      // The error shows from the mutation state.
    }
  };

  const apply = async () => {
    try {
      await applyScope.mutateAsync(ref);
      scope.refetch();
      setDialog(null);
    } catch {
      // The error shows from the mutation state.
    }
  };

  const outOfScope = scope.data?.outOfScope;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        paddingTop: 16,
        borderTop: "1px solid var(--bfc-base-c-dimmed)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <strong>Provisioning</strong>
        <div className="bf-small" style={{ color: "var(--bfc-base-c-2)" }}>
          {isScim ? "SCIM push" : "Graph pull"}
        </div>
        <div>
          <Button small variant="basic" onClick={() => setDialog("mode")}>
            {isScim ? "Switch to Graph" : "Switch to SCIM"}
          </Button>
        </div>
      </div>

      {isScim && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <strong>Tokens</strong>
            {tokens.data?.length === 0 && (
              <div
                className="bf-small"
                style={{ color: "var(--bfc-base-c-2)" }}
              >
                No live token
              </div>
            )}
            {tokens.data?.map((t) => (
              <div
                key={t.id}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <span className="bf-small">
                  Created {new Date(t.createdAt).toLocaleString()}
                </span>
                <Button
                  small
                  variant="basic"
                  state={revokeToken.isPending ? "inactive" : "alert"}
                  onClick={() => revokeToken.mutate({ ...ref, tokenId: t.id })}
                >
                  Revoke
                </Button>
              </div>
            ))}
            <div>
              <Button
                small
                variant="basic"
                state={createToken.isPending ? "inactive" : undefined}
                onClick={async () => {
                  try {
                    const created = await createToken.mutateAsync(ref);
                    setShownToken(created.token);
                  } catch {
                    // The error shows from the mutation state.
                  }
                }}
              >
                New token
              </Button>
            </div>
            <ErrorMessage error={createToken.error ?? revokeToken.error} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <strong>Scope</strong>
            <div className="bf-small" style={{ color: "var(--bfc-base-c-2)" }}>
              {outOfScope === undefined
                ? "Not checked"
                : `${outOfScope} users not named by the tenant`}
              {applyScope.data && ` · disabled ${applyScope.data.disabled}`}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button small variant="basic" onClick={() => scope.refetch()}>
                Preview
              </Button>
              {outOfScope !== undefined && outOfScope > 0 && (
                <Button
                  small
                  variant="basic"
                  state="alert"
                  onClick={() => setDialog("apply")}
                >
                  Apply
                </Button>
              )}
            </div>
            <ErrorMessage error={scope.error} />
          </div>
        </>
      )}

      {dialog === "mode" && (
        <Confirm
          header={isScim ? "Switch to Graph pull" : "Switch to SCIM push"}
          confirmLabel="Switch"
          destructive={isScim}
          pending={setMode.isPending || createToken.isPending}
          error={setMode.error ?? createToken.error}
          onConfirm={switchMode}
          onClose={close}
        >
          {isScim
            ? "The user sync pulls this tenant again, starting with a full pull. All its SCIM tokens are revoked, so the tenant's provisioning job stops."
            : "The user sync stops pulling this tenant. Its users then arrive only through SCIM push. If the tenant has no live token, one is created and shown once."}
        </Confirm>
      )}

      {dialog === "apply" && (
        <Confirm
          header="Disable out-of-scope users"
          confirmLabel={`Disable ${outOfScope} users`}
          pending={applyScope.isPending}
          destructive
          error={applyScope.error}
          onConfirm={apply}
          onClose={close}
        >
          This disables {outOfScope} users that the tenant no longer names. A
          later push of a user, or a switch back to Graph, enables the user
          again.
        </Confirm>
      )}

      {shownToken && (
        <TokenDialog token={shownToken} onClose={() => setShownToken(null)} />
      )}
    </div>
  );
}
